import { useState, useEffect, useRef } from "react";
import "@/App.css";
import emailjs from '@emailjs/browser';
import { motion, useInView } from 'framer-motion';
import { 
  Cpu, 
  Monitor, 
  FlaskConical, 
  Laptop, 
  Wrench,
  MapPin,
  Phone,
  Mail,
  Menu,
  X,
  Send,
  ChevronRight,
  Target,
  Eye,
  Award,
  Building2,
  Factory,
  Microscope,
  User,
  CheckCircle2,
  Crosshair,
  Shield,
  Lightbulb,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { Toaster as SonnerToaster } from 'sonner';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_np350bp';
const EMAILJS_TEMPLATE_ID = 'template_63fzr9z';
const EMAILJS_PUBLIC_KEY = 'l_QDBmH1dJ8WYAUBN';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#nosotros", label: "Nosotros" },
    { href: "#servicios", label: "Servicios" },
    { href: "#mercado", label: "Clientes" },
    { href: "#contacto", label: "Contacto" }
  ];

  return (
    <>
      <header 
        data-testid="header"
        className="fixed top-0 w-full z-50 transition-all duration-300 bg-white backdrop-blur-xl border-b border-[#50164A]/10 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#inicio" className="flex items-center" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_magma-agency/artifacts/l38z8lqi_LOGO%20MAGMA%20PNG.png" 
                alt="MAGMA CREATIVA" 
                className="h-16 w-auto"
                style={{ height: '4rem' }}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="nav-link"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-[#50164A] p-2"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 text-[#50164A] p-2"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="close-mobile-menu"
          >
            <X size={24} />
          </button>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <a 
            href="#contacto" 
            className="btn-primary mt-4"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="mobile-cta"
          >
            Contáctanos
          </a>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="inicio" 
      className="hero-bg min-h-screen flex items-center relative overflow-hidden"
      style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_magma-agency/artifacts/6zu7tz4o_magma-creativa.jpg)' }}
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-32 pt-40">
        <AnimatedSection>
          <motion.p 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] text-[#61CBF4] mb-6"
          >
            Soluciones Tecnológicas Avanzadas
          </motion.p>
          <motion.h1 
            variants={fadeInUp}
            className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6 max-w-4xl text-white"
          >
            MAGMA CREATIVA
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-lg sm:text-xl text-white/80 max-w-2xl mb-8 leading-relaxed"
          >
            Consultoría, investigación y mantenimiento técnico de alta precisión. 
            Transformamos la complejidad técnica en operatividad inmediata.
          </motion.p>
        </AnimatedSection>
      </div>

      {/* Decorative gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
    </section>
  );
};

// Services data
const services = [
  {
    icon: Cpu,
    title: "Mantenimiento de Equipo de Precisión",
    description: "Reparación especializada de sistemas electrónicos críticos y dispositivos de alta tecnología con rigor milimétrico.",
    span: "md:col-span-4 lg:col-span-5"
  },
  {
    icon: Monitor,
    title: "Consultoría en Computación",
    description: "Asesoría técnica para optimización de sistemas informáticos y gestión eficiente de recursos tecnológicos.",
    span: "md:col-span-4 lg:col-span-4"
  },
  {
    icon: FlaskConical,
    title: "Investigación y Desarrollo",
    description: "Servicios científicos en ciencias físicas y de la vida para el desarrollo de nuevas soluciones técnicas.",
    span: "md:col-span-8 lg:col-span-3"
  },
  {
    icon: Laptop,
    title: "Alquiler de Equipo",
    description: "Provisión de infraestructura de cómputo y máquinas de oficina bajo esquemas flexibles para tu empresa.",
    span: "md:col-span-4 lg:col-span-6"
  },
  {
    icon: Wrench,
    title: "Soporte Técnico Doméstico",
    description: "Reparación y mantenimiento preventivo de electrónica de consumo con calidad de grado industrial.",
    span: "md:col-span-4 lg:col-span-6"
  }
];

// Services Section
const ServicesSection = () => {
  return (
    <section 
      id="servicios" 
      className="py-24 sm:py-32 bg-[#F8F9FC]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#A02B93] mb-4">
              Lo Que Ofrecemos
            </p>
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#487DB0]">
              Nuestros Servicios
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`bento-card ${service.span}`}
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon">
                  <service.icon size={26} />
                </div>
                <h3 className="font-['Unbounded'] text-lg font-semibold mb-3 text-[#50164A]">
                  {service.title}
                </h3>
                <p className="text-[#5A5A72] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Market & Clients Section
const clientSegments = [
  {
    icon: Microscope,
    title: "Laboratorios de I+D",
    description: "Nos enfocamos en laboratorios de investigación y desarrollo (R&D) que exigen una alta fidelidad técnica y científica."
  },
  {
    icon: Building2,
    title: "Corporativos y Empresas",
    description: "Atendemos a corporativos y empresas que requieren consultoría en computación y mantenimiento de equipo de precisión para su continuidad operativa."
  },
  {
    icon: Factory,
    title: "Sector Industrial",
    description: "Buscamos socios en el sector industrial que no pueden permitirse tiempos muertos en su infraestructura electrónica de alto costo."
  },
  {
    icon: User,
    title: "Usuario Doméstico de Alto Perfil",
    description: "Servimos al usuario doméstico de alto perfil que valora una reparación profesional, garantizada y con rigor de ingeniería para su equipo personal."
  }
];

const MarketSection = () => {
  return (
    <section 
      id="mercado" 
      className="py-24 sm:py-32 bg-white relative overflow-hidden"
      data-testid="market-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#61CBF4]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#A02B93]/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#A02B93] mb-4">
              A Quiénes Servimos
            </p>
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6 text-[#487DB0]">
              Mercado Potencial y Clientes
            </h2>
            <p className="text-[#5A5A72] max-w-2xl mx-auto leading-relaxed">
              Nuestra experiencia nos permite atender a diversos sectores con la misma excelencia y compromiso técnico.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {clientSegments.map((segment, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative p-8 rounded-2xl bg-[#F8F9FC] border border-[#50164A]/10 hover:border-[#A02B93]/30 transition-all duration-300 hover:shadow-lg"
                data-testid={`client-segment-${index}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#A02B93] to-[#61CBF4] mb-6 group-hover:scale-110 transition-transform duration-300">
                  <segment.icon size={28} className="text-white" />
                </div>
                
                {/* Content */}
                <h3 className="font-['Unbounded'] text-xl font-semibold mb-4 text-[#50164A]">
                  {segment.title}
                </h3>
                <p className="text-[#5A5A72] leading-relaxed">
                  {segment.description}
                </p>

                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#A02B93]/5 to-[#61CBF4]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [activeTab, setActiveTab] = useState('mision');

  const tabs = [
    { 
      id: 'mision', 
      label: 'Misión', 
      icon: Target,
      content: 'Proveer servicios profesionales técnicos y científicos de excelencia, resolviendo fallas complejas y optimizando infraestructuras tecnológicas mediante el mantenimiento preventivo y la consultoría estratégica para el éxito de nuestros clientes.'
    },
    { 
      id: 'vision', 
      label: 'Visión', 
      icon: Eye,
      content: 'Consolidarnos en el estado como el centro tecnológico de referencia en servicios de reparación y desarrollo científico, siendo reconocidos por nuestra capacidad de respuesta técnica y la integración de soluciones de cómputo de vanguardia a nivel nacional.'
    }
  ];

  return (
    <section 
      id="nosotros" 
      className="py-24 sm:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/3825584/pexels-photo-3825584.jpeg"
                  alt="Técnico trabajando en equipo electrónico"
                  className="w-full h-full object-cover"
                  data-testid="about-image"
                />
              </div>
              {/* Decorative border */}
              <div className="absolute -inset-4 border border-[#A02B93]/20 rounded-3xl -z-10" />
            </motion.div>

            {/* Content */}
            <motion.div variants={fadeInUp}>
              <p className="text-sm uppercase tracking-[0.2em] text-[#A02B93] mb-4">
                Sobre Nosotros
              </p>
              <h2 className="font-['Unbounded'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6 text-[#487DB0]">
                Soluciones de Ingeniería de Alto Nivel
              </h2>
              <p className="text-[#5A5A72] mb-8 leading-relaxed">
                Somos una unidad de soluciones tecnológicas avanzadas dedicada a la consultoría, 
                investigación y mantenimiento técnico de alta precisión. Nos especializamos en la 
                reparación de equipo electrónico complejo, tanto de uso doméstico como de precisión 
                industrial.
              </p>

              {/* Tabs */}
              <div className="border-b border-[#50164A]/10 mb-6">
                <div className="flex gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`tab-trigger flex items-center gap-2 ${activeTab === tab.id ? 'active' : ''}`}
                      data-testid={`tab-${tab.id}`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="min-h-[100px]" data-testid="tab-content">
                {tabs.map((tab) => (
                  activeTab === tab.id && (
                    <motion.p
                      key={tab.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-[#5A5A72] leading-relaxed"
                    >
                      {tab.content}
                    </motion.p>
                  )
                ))}
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Values data
const values = [
  {
    icon: Crosshair,
    title: "Precisión Técnica",
    description: "Ejecutamos cada diagnóstico y reparación con un rigor milimétrico, utilizando herramientas de medición de última generación."
  },
  {
    icon: Shield,
    title: "Integridad en el Servicio",
    description: "Garantizamos transparencia total en el mantenimiento de equipos, asegurando el uso de componentes adecuados y procesos certificados."
  },
  {
    icon: Lightbulb,
    title: "Innovación en R&D",
    description: "Fomentamos la investigación y desarrollo constante para mantenernos a la cabeza de las nuevas tecnologías electrónicas y de precisión."
  },
  {
    icon: Clock,
    title: "Compromiso de Disponibilidad",
    description: "Ofrecemos soluciones ágiles de alquiler de equipo y soporte técnico para que la operación de nuestros clientes nunca se detenga."
  }
];

// Values Section
const ValuesSection = () => {
  return (
    <section 
      id="valores" 
      className="py-24 sm:py-32 bg-[#F8F9FC]"
      data-testid="values-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#A02B93] mb-4">
              Lo Que Nos Define
            </p>
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#487DB0]">
              Nuestros Valores
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex gap-5 p-6 rounded-xl bg-white border border-[#50164A]/10 hover:border-[#A02B93]/30 transition-all duration-300 group hover:shadow-lg"
                data-testid={`value-card-${index}`}
              >
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#61CBF4] to-[#487DB0] group-hover:scale-110 transition-transform duration-300">
                  <value.icon size={24} className="text-white" />
                </div>
                
                {/* Content */}
                <div>
                  <h3 className="font-['Unbounded'] text-lg font-semibold mb-2 text-[#50164A]">
                    {value.title}
                  </h3>
                  <p className="text-[#5A5A72] leading-relaxed text-sm">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.user_name.trim() || !formData.user_email.trim() || !formData.message.trim()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.user_email)) {
      toast.error('Por favor ingresa un correo electrónico válido');
      return;
    }

    setIsSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.user_name,
          from_email: formData.user_email,
          service: formData.service || 'No especificado',
          message: formData.message,
          reply_to: formData.user_email
        },
        EMAILJS_PUBLIC_KEY
      );

      toast.success('¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.');
      setFormData({
        user_name: '',
        user_email: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Hubo un error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Dirección',
      content: 'Juan Ruiz de Alarcón 129ª, INT -2, Col. Lafayette CP. 44150, Guadalajara, Jalisco, México'
    },
    {
      icon: Phone,
      title: 'Teléfono',
      content: '33 5119 8119'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contacto@magmacreativa.com.mx'
    }
  ];

  return (
    <section 
      id="contacto" 
      className="py-24 sm:py-32 bg-[#61CBF4] relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-white mb-4">
              Contacto
            </p>
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white">
              ¿Listo para comenzar?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h3 className="font-['Unbounded'] text-xl font-semibold mb-6 text-[#50164A]">
                Información de Contacto
              </h3>
              
              {contactInfo.map((item, index) => (
                <div 
                  key={index} 
                  className="contact-info-item bg-white/90"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="contact-info-icon">
                    <item.icon size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#50164A]/70 mb-1">{item.title}</p>
                    <p className="text-[#50164A]">{item.content}</p>
                  </div>
                </div>
              ))}

              <div className="pt-6">
                <p className="text-[#50164A]/80 leading-relaxed">
                  Nuestro equipo de expertos está listo para ayudarte con cualquier 
                  necesidad técnica. No dudes en contactarnos para una consulta gratuita.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={fadeInUp}>
              <form 
                ref={formRef}
                onSubmit={handleSubmit}
                className="contact-form space-y-6"
                data-testid="contact-form"
              >
                <div>
                  <label htmlFor="user_name" className="block text-sm text-[#5A5A72] mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="user_name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    required
                    disabled={isSubmitting}
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label htmlFor="user_email" className="block text-sm text-[#5A5A72] mb-2">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    id="user_email"
                    name="user_email"
                    value={formData.user_email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    disabled={isSubmitting}
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm text-[#5A5A72] mb-2">
                    Servicio de Interés
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    data-testid="select-service"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="mantenimiento">Mantenimiento de Equipo de Precisión</option>
                    <option value="consultoria">Consultoría en Computación</option>
                    <option value="rd">Investigación y Desarrollo</option>
                    <option value="alquiler">Alquiler de Equipo</option>
                    <option value="soporte">Soporte Técnico Doméstico</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-[#5A5A72] mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu proyecto o necesidad..."
                    rows="5"
                    required
                    disabled={isSubmitting}
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer 
      className="py-12 border-t border-[#50164A]/10 bg-white"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <a href="#inicio">
            <img 
              src="https://customer-assets.emergentagent.com/job_magma-agency/artifacts/l38z8lqi_LOGO%20MAGMA%20PNG.png" 
              alt="MAGMA CREATIVA" 
              className="h-16 w-auto"
              style={{ height: '4rem' }}
            />
          </a>

          {/* Links */}
          <nav className="flex flex-wrap justify-center gap-6">
            <a href="#inicio" className="footer-link">Inicio</a>
            <a href="#servicios" className="footer-link">Servicios</a>
            <a href="#nosotros" className="footer-link">Nosotros</a>
            <a href="#contacto" className="footer-link">Contacto</a>
          </nav>

          {/* Copyright */}
          <p className="text-sm text-[#50164A]">
            © {currentYear} MAGMA CREATIVA. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-white min-h-screen">
      <SonnerToaster 
        position="top-right" 
        theme="light"
        richColors
        toastOptions={{
          style: {
            background: '#FFFFFF',
            color: '#1A1A2E',
            border: '1px solid rgba(80, 22, 74, 0.1)'
          }
        }}
      />
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <ValuesSection />
        <ServicesSection />
        <MarketSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
